import { memo } from 'react';

import { motion } from 'motion/react';

import { TextAnimate } from '@shared/ui/text-animate';
import { Heading } from '@shared/ui/typography';

import type { VisionItemData } from '../../model/content';

interface LandingVisionItemProps extends VisionItemData {
  isActive: boolean;
}

const visionItemMotionVariants = {
  inactive: {
    opacity: 0,
    y: 12,
    visibility: 'hidden',
    transition: {
      duration: 0.2,
      ease: [0.4, 0, 1, 1] as const,
    },
    transitionEnd: {
      visibility: 'hidden',
    },
  },
  active: {
    opacity: 1,
    y: 0,
    visibility: 'visible',
    transition: {
      duration: 0.35,
      ease: [0.2, 1, 0.4, 1] as const,
    },
  },
} as const;

const visionCopyMotionVariants = {
  inactive: {
    transition: {
      staggerChildren: 0.03,
      staggerDirection: -1,
    },
  },
  active: {
    transition: {
      delayChildren: 0.06,
      staggerChildren: 0.04,
    },
  },
} as const;

const visionCopyItemMotionVariants = {
  inactive: {
    opacity: 0,
    y: 6,
  },
  active: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.28,
      ease: [0.2, 1, 0.4, 1] as const,
    },
  },
} as const;

const LandingVisionItemComponent = ({
  title,
  intro,
  description,
  titleAnimation,
  titleAnimationBy,
  titleAnimationDuration,
  isActive,
}: LandingVisionItemProps) => {
  return (
    <motion.article
      animate={isActive ? 'active' : 'inactive'}
      aria-hidden={!isActive}
      className="
        w-full border-border/0
        max-sm:pe-1
      "
      initial={false}
      variants={visionItemMotionVariants}
    >
      <Heading tag="h2" size="4xl" className="overflow-hidden text-foreground">
        <TextAnimate
          as="span"
          animation={titleAnimation}
          by={titleAnimationBy}
          animate="show"
          duration={titleAnimationDuration}
          className="block"
          initial={false}
          startOnView={false}
        >
          {title}
        </TextAnimate>
      </Heading>
      <motion.div variants={visionCopyMotionVariants}>
        <motion.p
          variants={visionCopyItemMotionVariants}
          className="
            mt-3 text-base/6 text-foreground-2
            md:text-lg
            lg:mt-5 lg:text-xl
          "
        >
          {intro}
        </motion.p>
        <motion.p
          variants={visionCopyItemMotionVariants}
          className="
            mt-2 text-base/6 text-foreground-2
            md:text-lg
            lg:mt-3 lg:text-xl
          "
        >
          {description}
        </motion.p>
      </motion.div>
    </motion.article>
  );
};

export const LandingVisionItem = memo(
  LandingVisionItemComponent,
  (prev, next) =>
    prev.isActive === next.isActive &&
    prev.title === next.title &&
    prev.intro === next.intro &&
    prev.description === next.description
);
